import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingCart, LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useSiteConfig } from "@/lib/siteConfig";

export default function Header() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const { config } = useSiteConfig();
  const [mobileOpen, setMobileOpen] = useState(false);
  const marquee = (config.marquee_items && config.marquee_items.length > 0)
    ? config.marquee_items
    : ["VENDITA STOCK E FALLIMENTI"];
  const navCats = config.nav_categories || [];
  const usedEnabled = !!config.used_market?.enabled;
  const close = () => setMobileOpen(false);
  const contactNav = config.contact_nav || { label: "Dove siamo", position: 1 };
  const menuItems = [
    { key: "home", label: "Home", to: "/", end: true },
    { key: "shop", label: "Catalogo", to: "/shop" },
    ...(usedEnabled ? [{ key: "used", label: "Mercatino", to: "/mercatino" }] : []),
    ...navCats.map((c, i) => ({ key: `cat-${i}-${c.value}`, label: c.label, to: `/shop?category=${encodeURIComponent(c.value)}` })),
  ];
  const contactPos = Math.min(Math.max(Number(contactNav.position ?? 1), 0), menuItems.length);
  menuItems.splice(contactPos, 0, { key: "contact", label: contactNav.label || "Dove siamo", to: "/dove-siamo" });

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-[var(--bs-border)]">
      <div className="bg-black text-white text-xs bs-marquee">
        <div className="bs-marquee-track py-2 font-mono-bs tracking-widest">
          {[...marquee, ...marquee, ...marquee].map((t, i) => (
            <span key={i} className={i % 2 ? "text-[var(--bs-red)]" : ""}>■ {t}</span>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2">
          <span className="font-display font-black text-3xl leading-none">
            <span className="text-black">B</span>
            <span className="text-[var(--bs-red)]">S</span>
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-display font-bold text-sm tracking-tight">Buti Stock</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--bs-muted)]">Stock & Fallimenti</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-semibold">
          {menuItems.map((item) => (
            <NavLink key={item.key} to={item.to} end={item.end} className={({isActive}) => `hover:text-[var(--bs-red)] ${isActive ? "text-[var(--bs-red)]" : ""}`}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-3 py-2 border border-black hover:bg-black hover:text-white transition-colors">
              <LayoutDashboard size={14} /> Admin
            </Link>
          )}
          {user?.role === "admin" && (
            <button onClick={logout} data-testid="btn-logout" className="p-2 hover:text-[var(--bs-red)]" aria-label="Esci"><LogOut size={16} /></button>
          )}
          <Link to="/cart" data-testid="nav-cart" className="relative p-2">
            <ShoppingCart size={20} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-[var(--bs-red)] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
          <button
            data-testid="btn-mobile-menu"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20}/> : <Menu size={20}/>}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[var(--bs-border)] bg-white" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col text-sm font-semibold">
            {menuItems.map((item) => (
              <NavLink key={item.key} to={item.to} end={item.end} onClick={close} className="py-3 border-b border-[var(--bs-border)] hover:text-[var(--bs-red)]">
                {item.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <Link to="/admin" onClick={close} className="py-3 border-b border-[var(--bs-border)] hover:text-[var(--bs-red)]" data-testid="mnav-admin">Admin</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
